import { useCallback, useEffect, useState, type ReactNode } from 'react';

import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import AdminToast from '../AdminToast';
import { AcademicPanelCard, AcademicPanelListItem, AcademicPanelTableWrap } from './AcademicPanelCard';
import {
  adjustPatrimoineStock,
  closeAffectationPatrimoine,
  createAffectationPatrimoine,
  createMaintenancePatrimoine,
  createPannePatrimoine,
  createPatrimoineArticle,
  createReservationSalle,
  deletePatrimoineArticle,
  fetchAffectationsPatrimoine,
  affecterPosteApprenant,
  fetchHistoriqueLogistique,
  fetchInscriptionsEnAttentePoste,
  fetchLogistiqueDashboard,
  fetchMaintenancesPatrimoine,
  fetchOrdinateursDisponiblesPoste,
  fetchPatrimoineArticles,
  fetchPannesPatrimoine,
  fetchPostesEtudiantsActifs,
  fetchPresentsSansPoste,
  fetchReservationsSalle,
  fetchSallesLogistique,
  libererPosteEtudiant,
  patchMaintenancePatrimoine,
  patchPannePatrimoine,
  patchReservationSalle,
} from '../../../services/academiaAdminService';

type TabId = 'vue' | 'inventaire' | 'stocks' | 'postes' | 'affectations' | 'maintenance' | 'salles' | 'historique';

const CATEGORIES = [
  { value: 'INFORMATIQUE', label: 'Informatique' },
  { value: 'MOBILIER', label: 'Mobilier' },
  { value: 'PEDAGOGIQUE', label: 'Pédagogique' },
  { value: 'CONSOMMABLE', label: 'Consommables' },
] as const;

const TYPES_MAINTENANCE = [
  { value: 'Préventive', label: 'Préventive' },
  { value: 'Corrective', label: 'Corrective' },
  { value: 'Curative', label: 'Curative' },
  { value: 'Mise à jour', label: 'Mise à jour (logicielle / matérielle)' },
  { value: 'Contrôle', label: 'Contrôle / inspection' },
  { value: 'Nettoyage', label: 'Nettoyage' },
  { value: 'Calibrage', label: 'Calibrage' },
  { value: 'Révision', label: 'Révision' },
  { value: 'Installation', label: 'Installation / remplacement' },
] as const;

const STATUT_LABELS: Record<string, string> = {
  EN_STOCK: 'En stock',
  AFFECTE: 'Affecté',
  EN_MAINTENANCE: 'Maintenance',
  EN_PANNE: 'En panne',
  HORS_SERVICE: 'Hors service',
};

function formatDateTime(iso: string) {
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

const fieldInputClass =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

function FormField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className ?? 'flex min-w-0 flex-col gap-1'}>
      <span className="text-xs font-semibold text-gray-600">{label}</span>
      {children}
    </div>
  );
}

/** Select patrimoine — même rendu que l’onglet « Nouvelle affectation ». */
function TablePaginationBar({
  itemCount,
  currentPage,
  totalPages,
  onPageChange,
  summary,
}: {
  itemCount: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  summary: string;
}) {
  if (itemCount === 0) return null;
  return (
    <AdminPagination
      className="border-t border-outline-variant/15 px-4 py-3"
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      summary={summary}
    />
  );
}

function PatrimoineFormSelect({
  value,
  onChange,
  placeholder = 'Choisir…',
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  children: ReactNode;
}) {
  return (
    <AcademiaSelect fieldSize="sm" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {children}
    </AcademiaSelect>
  );
}

export default function AcademiaLogistiquePanel() {
  const [tab, setTab] = useState<TabId>('vue');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [dashboard, setDashboard] = useState<Record<string, unknown> | null>(null);
  const [articles, setArticles] = useState<Record<string, unknown>[]>([]);
  const [salles, setSalles] = useState<Record<string, unknown>[]>([]);
  const [affectations, setAffectations] = useState<Record<string, unknown>[]>([]);
  const [maintenances, setMaintenances] = useState<Record<string, unknown>[]>([]);
  const [pannes, setPannes] = useState<Record<string, unknown>[]>([]);
  const [reservations, setReservations] = useState<Record<string, unknown>[]>([]);
  const [historique, setHistorique] = useState<Record<string, unknown>[]>([]);
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [searchQ, setSearchQ] = useState('');

  const [articleForm, setArticleForm] = useState({
    reference: '',
    libelle: '',
    categorie: 'INFORMATIQUE',
    sousType: '',
    quantiteStock: '1',
    seuilAlerte: '0',
    idSalle: '',
  });
  const [stockDelta, setStockDelta] = useState<Record<string, string>>({});
  const [affForm, setAffForm] = useState({ idArticle: '', idSalle: '', motif: '' });
  const [postesEnAttente, setPostesEnAttente] = useState<Record<string, unknown>[]>([]);
  const [postesActifs, setPostesActifs] = useState<Record<string, unknown>[]>([]);
  const [ordinateursDispo, setOrdinateursDispo] = useState<Record<string, unknown>[]>([]);
  const [presentsSansPoste, setPresentsSansPoste] = useState<Record<string, unknown>[]>([]);
  const [posteForm, setPosteForm] = useState({ idInscription: '', idArticle: '' });
  const [maintForm, setMaintForm] = useState({ idArticle: '', typeMaintenance: '', description: '' });
  const [panneForm, setPanneForm] = useState({ idArticle: '', description: '', gravite: 'MOYENNE' });
  const [resaForm, setResaForm] = useState({
    idSalle: '',
    titre: '',
    dateDebut: '',
    dateFin: '',
    participantsEstimes: '',
    equipementsRequis: [] as { idArticle: string; quantite: string }[],
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dash, arts, sal, aff, maint, pan, resa, hist, attente, actifs, pcs, sansPoste] =
        await Promise.all([
        fetchLogistiqueDashboard(),
        fetchPatrimoineArticles({
          categorie: filtreCategorie || undefined,
          q: searchQ || undefined,
        }),
        fetchSallesLogistique(),
        fetchAffectationsPatrimoine(),
        fetchMaintenancesPatrimoine(),
        fetchPannesPatrimoine(),
        fetchReservationsSalle(),
        fetchHistoriqueLogistique(),
        fetchInscriptionsEnAttentePoste(),
        fetchPostesEtudiantsActifs(),
        fetchOrdinateursDisponiblesPoste(),
        fetchPresentsSansPoste(),
      ]);
      setDashboard(dash);
      setArticles(arts);
      setSalles(sal);
      setAffectations(aff);
      setMaintenances(maint);
      setPannes(pan);
      setReservations(resa);
      setHistorique(hist);
      setPostesEnAttente(attente);
      setPostesActifs(actifs);
      setOrdinateursDispo(pcs);
      setPresentsSansPoste(sansPoste);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [filtreCategorie, searchQ]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useRegisterPortalRefresh(() => void loadAll());

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const runAction = useCallback(
    async (
      action: () => Promise<void>,
      successMessage: string,
      errorFallback = 'Action impossible.',
      options?: { reload?: boolean },
    ) => {
      try {
        await action();
        showToast(successMessage, 'success');
        if (options?.reload !== false) {
          await loadAll();
        }
      } catch (e) {
        showToast(e instanceof Error ? e.message : errorFallback, 'error');
      }
    },
    [loadAll, showToast],
  );

  const handleCreateArticle = async () => {
    await runAction(async () => {
      await createPatrimoineArticle({
        ...articleForm,
        quantiteStock: Number(articleForm.quantiteStock),
        seuilAlerte: Number(articleForm.seuilAlerte),
        idSalle: articleForm.idSalle || null,
        estConsommable: articleForm.categorie === 'CONSOMMABLE',
      });
      setArticleForm({
        reference: '',
        libelle: '',
        categorie: 'INFORMATIQUE',
        sousType: '',
        quantiteStock: '1',
        seuilAlerte: '0',
        idSalle: '',
      });
    }, "Article ajouté à l'inventaire");
  };

  const handleStockAdjust = async (id: string) => {
    const delta = Number(stockDelta[id] ?? 0);
    if (!delta) {
      showToast('Indiquez une quantité à ajouter ou retirer.', 'error');
      return;
    }
    await runAction(
      () => adjustPatrimoineStock(id, delta, 'Ajustement manuel'),
      'Stock mis à jour',
    );
  };

  const handleFilterInventaire = async () => {
    try {
      await loadAll();
      showToast('Inventaire actualisé', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Filtrage impossible', 'error');
    }
  };

  const stocksAlerte = (dashboard?.alertesStock as Record<string, unknown>[]) ?? [];
  const consommables = articles.filter((a) => a.estConsommable);

  const articlesPagination = useAdminTablePagination(articles);
  const consommablesPagination = useAdminTablePagination(consommables);
  const postesActifsPagination = useAdminTablePagination(postesActifs);
  const affectationsPagination = useAdminTablePagination(affectations);
  const maintenancesPagination = useAdminTablePagination(maintenances);
  const pannesPagination = useAdminTablePagination(pannes);
  const reservationsPagination = useAdminTablePagination(reservations);
  const historiquePagination = useAdminTablePagination(historique);

  const salleSelectionnee = salles.find((s) => String(s.id) === resaForm.idSalle) as
    | { equipements?: { article?: { id?: string; libelle?: string }; quantite?: number }[] }
    | undefined;

  const sideCard = {
    label: 'Articles inventoriés',
    value: loading ? '…' : String(dashboard?.totalArticles ?? 0),
    helper: 'Patrimoine, stocks et salles',
    sideLinks: [
      { label: 'Planning', to: '/admin/planning', icon: 'calendar_month' },
      { label: 'Formations', to: '/admin/formations', icon: 'school' },
    ],
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'vue', label: 'Vue d\'ensemble' },
    { id: 'inventaire', label: 'Inventaire' },
    { id: 'stocks', label: 'Stocks & alertes' },
    { id: 'postes', label: 'Postes étudiants' },
    { id: 'affectations', label: 'Affectations' },
    { id: 'maintenance', label: 'Maintenance & pannes' },
    { id: 'salles', label: 'Salles & réservations' },
    { id: 'historique', label: 'Historique' },
  ];

  const kpiItems = dashboard
    ? [
        { label: 'Articles', value: dashboard.totalArticles, icon: 'inventory_2', tab: 'inventaire' as TabId },
        {
          label: 'Alertes rupture',
          value: dashboard.alertesRupture,
          icon: 'warning',
          tab: 'stocks' as TabId,
          warn: Number(dashboard.alertesRupture ?? 0) > 0,
        },
        { label: 'Pannes ouvertes', value: dashboard.pannesOuvertes, icon: 'settings', tab: 'maintenance' as TabId },
        {
          label: 'Maintenances actives',
          value: dashboard.maintenancesActives,
          icon: 'tune',
          tab: 'maintenance' as TabId,
        },
        {
          label: 'Réservations aujourd\'hui',
          value: dashboard.reservationsJour,
          icon: 'event',
          tab: 'salles' as TabId,
        },
        {
          label: 'Postes à attribuer',
          value: dashboard.postesEnAttente,
          icon: 'smartphone',
          tab: 'postes' as TabId,
          warn: Number(dashboard.postesEnAttente ?? 0) > 0,
        },
      ]
    : [];

  return (
    <AdminPageShell
      title="Logistique & patrimoine"
      sideCard={sideCard}
      description="Inventaire numérique, stocks, affectations, maintenance, réservation des salles."
      onRetry={() => void loadAll()}
      isLoading={loading}
      isError={!!error}
      loadingMessage="Chargement logistique…"
      errorMessage={error || 'Impossible de charger la logistique.'}
    >
      <div className="logistique-panel-tabs" role="tablist" aria-label="Sections logistique">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`logistique-panel-tab ${tab === t.id ? 'logistique-panel-tab--active' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'vue' && dashboard ? (
        <div className="logistique-kpi-grid">
          {kpiItems.map((kpi) => (
            <button
              key={kpi.label}
              type="button"
              className={`logistique-kpi-card ${kpi.warn ? 'logistique-kpi-card--warn' : ''}`}
              onClick={() => setTab(kpi.tab)}
            >
              <div className="logistique-kpi-card__top">
                <span className="logistique-kpi-card__icon" aria-hidden>
                  <LucideSymbolIcon name={kpi.icon} tone="inherit" size={20} />
                </span>
                <span className="logistique-kpi-card__hint">Voir →</span>
              </div>
              <span className="logistique-kpi-card__value">{String(kpi.value ?? 0)}</span>
              <span className="logistique-kpi-card__label">{kpi.label}</span>
            </button>
          ))}
        </div>
      ) : null}

      {tab === 'inventaire' ? (
        <div className="space-y-6">
          <div className="logistique-filter-bar">
            <FormField label="Recherche" className="min-w-[12rem] flex-1">
              <input
                type="search"
                placeholder="Référence, libellé…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className={fieldInputClass}
              />
            </FormField>
            <FormField label="Catégorie" className="min-w-[11rem]">
              <AcademiaSelect
                fieldSize="sm"
                value={filtreCategorie}
                onChange={(e) => setFiltreCategorie(e.target.value)}
              >
                <option value="">Toutes catégories</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </AcademiaSelect>
            </FormField>
            <div className="logistique-filter-bar__actions">
              <button
                type="button"
                onClick={() => void handleFilterInventaire()}
                className="academic-panel-btn academic-panel-btn--primary"
              >
                Filtrer
              </button>
            </div>
          </div>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="add_circle" tone="inherit" size={22} />}
            title="Nouvel article"
            subtitle="Ajouter une référence au patrimoine"
            variant="accent"
            footer={
              <button
                type="button"
                onClick={() => void handleCreateArticle()}
                className="academic-panel-btn academic-panel-btn--primary"
              >
                Enregistrer
              </button>
            }
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <FormField label="Référence *">
                <input
                  value={articleForm.reference}
                  onChange={(e) => setArticleForm((f) => ({ ...f, reference: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Libellé *">
                <input
                  value={articleForm.libelle}
                  onChange={(e) => setArticleForm((f) => ({ ...f, libelle: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Catégorie *">
                <AcademiaSelect
                  fieldSize="sm"
                  value={articleForm.categorie}
                  onChange={(e) => setArticleForm((f) => ({ ...f, categorie: e.target.value }))}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </AcademiaSelect>
              </FormField>
              <FormField label="Sous-type">
                <input
                  placeholder="ex. ordinateur"
                  value={articleForm.sousType}
                  onChange={(e) => setArticleForm((f) => ({ ...f, sousType: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Quantité en stock">
                <input
                  type="number"
                  value={articleForm.quantiteStock}
                  onChange={(e) => setArticleForm((f) => ({ ...f, quantiteStock: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Seuil d'alerte">
                <input
                  type="number"
                  value={articleForm.seuilAlerte}
                  onChange={(e) => setArticleForm((f) => ({ ...f, seuilAlerte: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Salle (emplacement)">
                <AcademiaSelect
                  fieldSize="sm"
                  value={articleForm.idSalle}
                  onChange={(e) => setArticleForm((f) => ({ ...f, idSalle: e.target.value }))}
                >
                  <option value="">Aucune</option>
                  {salles.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>
                      {String(s.libelle)}
                    </option>
                  ))}
                </AcademiaSelect>
              </FormField>
            </div>
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="inventory_2" tone="inherit" size={22} />}
            title="Inventaire"
            subtitle={`${articles.length} article(s) affiché(s)`}
            flush
          >
          <AcademicPanelTableWrap empty={articles.length === 0 ? 'Aucun article.' : undefined}>
            <table className="academic-panel-table">
              <thead>
                <tr>
                  <th className="px-3 py-2">Réf.</th>
                  <th className="px-3 py-2">Libellé</th>
                  <th className="px-3 py-2">Catégorie</th>
                  <th className="px-3 py-2">Stock</th>
                  <th className="px-3 py-2">Statut</th>
                  <th className="px-3 py-2">Salle</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articlesPagination.paginatedItems.map((a) => {
                  const salle = a.salle as { libelle?: string } | null;
                  const reference = String(a.reference);
                  const libelle = String(a.libelle);
                  return (
                    <tr key={String(a.id)}>
                      <td className="font-mono text-xs">{reference}</td>
                      <td className="px-3 py-2">{libelle}</td>
                      <td className="px-3 py-2">{String(a.categorie)}</td>
                      <td className="px-3 py-2">{String(a.quantiteStock)}</td>
                      <td className="px-3 py-2">{STATUT_LABELS[String(a.statut)] ?? String(a.statut)}</td>
                      <td className="px-3 py-2">{salle?.libelle ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <AdminTableDeleteButton
                          canDelete
                          confirmMessage={`Supprimer définitivement « ${reference} — ${libelle} » de l'inventaire ? Cette action est irréversible.`}
                          onDelete={async () => {
                            await runAction(
                              () => deletePatrimoineArticle(String(a.id)),
                              `« ${reference} » retiré de l'inventaire`,
                            );
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AcademicPanelTableWrap>
          <TablePaginationBar
            itemCount={articles.length}
            currentPage={articlesPagination.currentPage}
            totalPages={articlesPagination.totalPages}
            onPageChange={articlesPagination.onPageChange}
            summary={articlesPagination.summary}
          />
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'stocks' ? (
        <div className="space-y-4">
          <AcademicPanelCard
            icon={<LucideSymbolIcon name="warning" tone="inherit" size={22} />}
            title="Alertes de rupture"
            subtitle="Consommables sous le seuil défini"
            variant={stocksAlerte.length > 0 ? 'accent' : 'default'}
          >
            {stocksAlerte.length > 0 ? (
              <ul className="m-0 list-none space-y-2 p-0">
                {stocksAlerte.map((a) => (
                  <li
                    key={String(a.id)}
                    className="rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-sm text-amber-950"
                  >
                    <strong>{String(a.reference)}</strong> — {String(a.libelle)} : stock{' '}
                    <strong>{String(a.quantiteStock)}</strong> / seuil {String(a.seuilAlerte)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-on-surface-variant">Aucune alerte de rupture.</p>
            )}
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="tune" tone="inherit" size={22} />}
            title="Ajustement des stocks"
            subtitle="Consommables — variation manuelle"
          >
            <ul className="academic-panel-list">
            {consommablesPagination.paginatedItems.map((a) => (
              <AcademicPanelListItem key={String(a.id)}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900">{String(a.reference)}</p>
                  <p className="text-xs text-on-surface-variant">Stock actuel : {String(a.quantiteStock)}</p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                <FormField label="Ajustement (±)" className="w-24">
                  <input
                    type="number"
                    className={fieldInputClass}
                    value={stockDelta[String(a.id)] ?? ''}
                    onChange={(e) => setStockDelta((d) => ({ ...d, [String(a.id)]: e.target.value }))}
                  />
                </FormField>
                <button
                  type="button"
                  onClick={() => void handleStockAdjust(String(a.id))}
                  className="academic-panel-btn academic-panel-btn--primary"
                >
                  Ajuster
                </button>
                </div>
              </AcademicPanelListItem>
            ))}
            </ul>
            <TablePaginationBar
              itemCount={consommables.length}
              currentPage={consommablesPagination.currentPage}
              totalPages={consommablesPagination.totalPages}
              onPageChange={consommablesPagination.onPageChange}
              summary={consommablesPagination.summary}
            />
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'postes' ? (
        <div className="space-y-6">
          <AcademicPanelCard
            icon={<LucideSymbolIcon name="smartphone" tone="inherit" size={22} />}
            title="Postes étudiants"
            subtitle="Un ordinateur par inscription jusqu'à la fin de la formation — attribution à l'accueil"
            variant="accent"
          >
            <p className="text-sm text-on-surface-variant">
              Libération automatique si l&apos;inscription est terminée, annulée ou désistée.
            </p>
          </AcademicPanelCard>

          {presentsSansPoste.length > 0 ? (
            <div className="logistique-alert-banner">
              <p className="flex items-center gap-2 text-sm font-bold text-amber-900">
                <LucideSymbolIcon name="warning" tone="inherit" size={18} />
                {presentsSansPoste.length} apprenant(s) présent(s) sans poste attribué
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-950">
                {presentsSansPoste.slice(0, 8).map((row) => {
                  const ins = row.inscription as {
                    matriculeSnapshot?: string;
                    apprenant?: { profil?: { prenom?: string; nom?: string } };
                    filiere?: { libelle?: string };
                  };
                  const name = [ins?.apprenant?.profil?.prenom, ins?.apprenant?.profil?.nom]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <li key={String(row.idInscription)}>
                      {name || ins?.matriculeSnapshot} — {ins?.filiere?.libelle ?? 'Formation'}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="person_add" tone="inherit" size={22} />}
            title="Attribuer un poste"
            subtitle="Accueil — sélection inscription et ordinateur"
            footer={
              <button
                type="button"
                disabled={!posteForm.idInscription || !posteForm.idArticle}
                onClick={() =>
                  void runAction(async () => {
                    await affecterPosteApprenant(posteForm);
                    setPosteForm({ idInscription: '', idArticle: '' });
                  }, 'Poste attribué à l’apprenant')
                }
                className="academic-panel-btn academic-panel-btn--primary disabled:opacity-50"
              >
                Attribuer le poste
              </button>
            }
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Inscription (sans poste)">
                <AcademiaSelect
                  fieldSize="sm"
                  value={posteForm.idInscription}
                  onChange={(e) => setPosteForm((f) => ({ ...f, idInscription: e.target.value }))}
                >
                  <option value="">Choisir…</option>
                  {postesEnAttente.map((ins) => {
                    const p = ins.apprenant as { profil?: { prenom?: string; nom?: string } };
                    const name = [p?.profil?.prenom, p?.profil?.nom].filter(Boolean).join(' ');
                    const fil = ins.filiere as { libelle?: string };
                    return (
                      <option key={String(ins.id)} value={String(ins.id)}>
                        {name || String(ins.matriculeSnapshot)} — {fil?.libelle ?? ''}
                      </option>
                    );
                  })}
                </AcademiaSelect>
              </FormField>
              <FormField label="Ordinateur disponible">
                <AcademiaSelect
                  fieldSize="sm"
                  value={posteForm.idArticle}
                  onChange={(e) => setPosteForm((f) => ({ ...f, idArticle: e.target.value }))}
                >
                  <option value="">Choisir…</option>
                  {ordinateursDispo.map((a) => (
                    <option key={String(a.id)} value={String(a.id)}>
                      {String(a.reference)} — {String(a.libelle)}
                    </option>
                  ))}
                </AcademiaSelect>
              </FormField>
            </div>
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="article" tone="inherit" size={22} />}
            title={`Postes actifs (${postesActifs.length})`}
            subtitle="Attributions en cours"
            flush={postesActifs.length > 0}
          >
            <ul className="academic-panel-list p-4">
              {postesActifs.length === 0 ? (
                <li className="py-4 text-center text-sm text-on-surface-variant">Aucun poste étudiant actif.</li>
              ) : (
                postesActifsPagination.paginatedItems.map((a) => {
                  const art = a.article as { reference?: string; libelle?: string };
                  const ins = a.inscription as {
                    matriculeSnapshot?: string;
                    apprenant?: { profil?: { prenom?: string; nom?: string } };
                    filiere?: { libelle?: string };
                  };
                  const name = [ins?.apprenant?.profil?.prenom, ins?.apprenant?.profil?.nom]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <AcademicPanelListItem key={String(a.id)}>
                      <span className="min-w-0 flex-1 text-sm">
                        <strong className="text-primary">{art?.reference}</strong> — {name || ins?.matriculeSnapshot}{' '}
                        <span className="text-on-surface-variant">({ins?.filiere?.libelle ?? 'Formation'})</span>
                      </span>
                      <button
                        type="button"
                        className="academic-panel-btn academic-panel-btn--ghost"
                        onClick={() =>
                          void runAction(
                            () => libererPosteEtudiant(String(a.id)),
                            'Poste informatique libéré',
                          )
                        }
                      >
                        Libérer
                      </button>
                    </AcademicPanelListItem>
                  );
                })
              )}
            </ul>
            <TablePaginationBar
              itemCount={postesActifs.length}
              currentPage={postesActifsPagination.currentPage}
              totalPages={postesActifsPagination.totalPages}
              onPageChange={postesActifsPagination.onPageChange}
              summary={postesActifsPagination.summary}
            />
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'affectations' ? (
        <div className="space-y-4">
          <AcademicPanelCard
            icon={<LucideSymbolIcon name="assignment" tone="inherit" size={22} />}
            title="Nouvelle affectation"
            subtitle="Équipement non consommable"
            variant="accent"
            footer={
              <button
                type="button"
                onClick={() =>
                  void runAction(
                    () => createAffectationPatrimoine(affForm),
                    'Affectation enregistrée',
                  )
                }
                className="academic-panel-btn academic-panel-btn--primary"
              >
                Affecter
              </button>
            }
          >
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField label="Article *">
              <PatrimoineFormSelect
                value={affForm.idArticle}
                onChange={(v) => setAffForm((f) => ({ ...f, idArticle: v }))}
              >
                {articles.filter((a) => !a.estConsommable).map((a) => (
                  <option key={String(a.id)} value={String(a.id)}>
                    {String(a.reference)}
                  </option>
                ))}
              </PatrimoineFormSelect>
            </FormField>
            <FormField label="Salle">
              <PatrimoineFormSelect
                value={affForm.idSalle}
                onChange={(v) => setAffForm((f) => ({ ...f, idSalle: v }))}
                placeholder="Aucune"
              >
                {salles.map((s) => (
                  <option key={String(s.id)} value={String(s.id)}>
                    {String(s.libelle)}
                  </option>
                ))}
              </PatrimoineFormSelect>
            </FormField>
            <FormField label="Motif">
              <input
                value={affForm.motif}
                onChange={(e) => setAffForm((f) => ({ ...f, motif: e.target.value }))}
                className={fieldInputClass}
              />
            </FormField>
          </div>
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="list_alt" tone="inherit" size={22} />}
            title="Historique des affectations"
            subtitle={`${affectations.length} enregistrement(s)`}
            flush={affectations.length > 0}
          >
          <ul className="academic-panel-list p-4">
            {affectationsPagination.paginatedItems.map((a) => {
              const art = a.article as { reference?: string; libelle?: string };
              return (
                <AcademicPanelListItem key={String(a.id)}>
                  <span className="min-w-0 flex-1 text-sm">
                    <strong>{art?.reference}</strong> — {a.motif ? String(a.motif) : 'Sans motif'}{' '}
                    <span className={a.estActive ? 'text-emerald-700' : 'text-on-surface-variant'}>
                      {a.estActive ? '(active)' : '(clôturée)'}
                    </span>
                  </span>
                  {a.estActive ? (
                    <button
                      type="button"
                      className="academic-panel-btn academic-panel-btn--ghost"
                      onClick={() =>
                        void runAction(
                          () => closeAffectationPatrimoine(String(a.id)),
                          'Affectation clôturée',
                        )
                      }
                    >
                      Clôturer
                    </button>
                  ) : null}
                </AcademicPanelListItem>
              );
            })}
          </ul>
          <TablePaginationBar
            itemCount={affectations.length}
            currentPage={affectationsPagination.currentPage}
            totalPages={affectationsPagination.totalPages}
            onPageChange={affectationsPagination.onPageChange}
            summary={affectationsPagination.summary}
          />
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'maintenance' ? (
        <div className="space-y-6">
          <AcademicPanelCard
            icon={<LucideSymbolIcon name="tune" tone="inherit" size={22} />}
            title="Planifier une maintenance"
            variant="accent"
            footer={
              <button
                type="button"
                disabled={!maintForm.idArticle || !maintForm.typeMaintenance}
                onClick={() =>
                  void runAction(async () => {
                    await createMaintenancePatrimoine(maintForm);
                    setMaintForm({ idArticle: '', typeMaintenance: '', description: '' });
                  }, 'Maintenance planifiée')
                }
                className="academic-panel-btn academic-panel-btn--primary disabled:opacity-50"
              >
                Planifier
              </button>
            }
          >
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField label="Article *">
              <PatrimoineFormSelect
                value={maintForm.idArticle}
                onChange={(v) => setMaintForm((f) => ({ ...f, idArticle: v }))}
              >
                {articles.map((a) => (
                  <option key={String(a.id)} value={String(a.id)}>
                    {String(a.reference)}
                  </option>
                ))}
              </PatrimoineFormSelect>
            </FormField>
            <FormField label="Type de maintenance *">
              <PatrimoineFormSelect
                value={maintForm.typeMaintenance}
                onChange={(v) => setMaintForm((f) => ({ ...f, typeMaintenance: v }))}
              >
                {TYPES_MAINTENANCE.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </PatrimoineFormSelect>
            </FormField>
            <FormField label="Description">
              <input
                value={maintForm.description}
                onChange={(e) => setMaintForm((f) => ({ ...f, description: e.target.value }))}
                className={fieldInputClass}
                placeholder="Optionnel"
              />
            </FormField>
          </div>
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="settings" tone="inherit" size={22} />}
            title="Maintenances"
            subtitle={`${maintenances.length} intervention(s)`}
            flush={maintenances.length > 0}
          >
          <ul className="academic-panel-list p-4">
            {maintenancesPagination.paginatedItems.map((m) => {
              const art = m.article as { reference?: string };
              return (
                <AcademicPanelListItem key={String(m.id)}>
                  <span className="text-sm">
                    {art?.reference} — {String(m.typeMaintenance)}{' '}
                    <span className="text-on-surface-variant">({String(m.statut)})</span>
                  </span>
                  {m.statut !== 'TERMINEE' ? (
                    <button
                      type="button"
                      className="academic-panel-btn academic-panel-btn--ghost"
                      onClick={() =>
                        void runAction(
                          () => patchMaintenancePatrimoine(String(m.id), { statut: 'TERMINEE' }),
                          'Maintenance terminée',
                        )
                      }
                    >
                      Terminer
                    </button>
                  ) : null}
                </AcademicPanelListItem>
              );
            })}
          </ul>
          <TablePaginationBar
            itemCount={maintenances.length}
            currentPage={maintenancesPagination.currentPage}
            totalPages={maintenancesPagination.totalPages}
            onPageChange={maintenancesPagination.onPageChange}
            summary={maintenancesPagination.summary}
          />
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="warning" tone="inherit" size={22} />}
            title="Signaler une panne"
            variant="accent"
            footer={
              <button
                type="button"
                onClick={() =>
                  void runAction(
                    () => createPannePatrimoine(panneForm),
                    'Panne signalée',
                  )
                }
                className="academic-panel-btn academic-panel-btn--primary"
              >
                Signaler
              </button>
            }
          >
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField label="Article *">
              <AcademiaSelect
                fieldSize="sm"
                value={panneForm.idArticle}
                onChange={(e) => setPanneForm((f) => ({ ...f, idArticle: e.target.value }))}
              >
                <option value="">Choisir…</option>
                {articles.map((a) => (
                  <option key={String(a.id)} value={String(a.id)}>
                    {String(a.reference)}
                  </option>
                ))}
              </AcademiaSelect>
            </FormField>
            <FormField label="Gravité">
              <AcademiaSelect
                fieldSize="sm"
                value={panneForm.gravite}
                onChange={(e) => setPanneForm((f) => ({ ...f, gravite: e.target.value }))}
              >
                <option value="FAIBLE">Faible</option>
                <option value="MOYENNE">Moyenne</option>
                <option value="ELEVEE">Élevée</option>
                <option value="CRITIQUE">Critique</option>
              </AcademiaSelect>
            </FormField>
            <FormField label="Description *" className="sm:col-span-1">
              <input
                value={panneForm.description}
                onChange={(e) => setPanneForm((f) => ({ ...f, description: e.target.value }))}
                className={fieldInputClass}
              />
            </FormField>
          </div>
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="error" tone="inherit" size={22} />}
            title="Pannes signalées"
            subtitle={`${pannes.length} ticket(s)`}
            flush={pannes.length > 0}
          >
          <ul className="academic-panel-list p-4">
            {pannesPagination.paginatedItems.map((p) => {
              const art = p.article as { reference?: string };
              return (
                <AcademicPanelListItem key={String(p.id)}>
                  <span className="text-sm">
                    {art?.reference} — {String(p.gravite)}{' '}
                    <span className="text-on-surface-variant">({String(p.statut)})</span>
                  </span>
                  {p.statut !== 'RESOLUE' ? (
                    <button
                      type="button"
                      className="academic-panel-btn academic-panel-btn--ghost"
                      onClick={() =>
                        void runAction(
                          () => patchPannePatrimoine(String(p.id), { statut: 'RESOLUE' }),
                          'Panne marquée comme résolue',
                        )
                      }
                    >
                      Résoudre
                    </button>
                  ) : null}
                </AcademicPanelListItem>
              );
            })}
          </ul>
          <TablePaginationBar
            itemCount={pannes.length}
            currentPage={pannesPagination.currentPage}
            totalPages={pannesPagination.totalPages}
            onPageChange={pannesPagination.onPageChange}
            summary={pannesPagination.summary}
          />
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'salles' ? (
        <div className="space-y-6">
          <div className="logistique-salle-grid">
            {salles.map((s) => (
              <article key={String(s.id)} className="logistique-salle-card">
                <div className="logistique-salle-card__head">
                  <span className="logistique-salle-card__icon" aria-hidden>
                    <LucideSymbolIcon name="location_city" tone="inherit" size={22} />
                  </span>
                  <div className="min-w-0">
                    <h4 className="logistique-salle-card__title">{String(s.libelle)}</h4>
                    <p className="logistique-salle-card__meta">
                      Capacité {String(s.capacite)} — {String(s.reservationsActives ?? 0)} réservation(s) active(s)
                    </p>
                  </div>
                </div>
                <p className="logistique-salle-card__meta">
                  <strong className="text-slate-700">Équipements :</strong>{' '}
                  {((s.equipements as { article?: { libelle?: string } }[]) ?? [])
                    .map((e) => e.article?.libelle)
                    .filter(Boolean)
                    .join(', ') || '—'}
                </p>
              </article>
            ))}
          </div>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="event" tone="inherit" size={22} />}
            title="Nouvelle réservation"
            subtitle="Planifier l'occupation d'une salle"
            variant="accent"
            footer={
              <button
                type="button"
                onClick={() =>
                  void runAction(async () => {
                    await createReservationSalle({
                      ...resaForm,
                      dateDebut: new Date(resaForm.dateDebut).toISOString(),
                      dateFin: new Date(resaForm.dateFin).toISOString(),
                      participantsEstimes: resaForm.participantsEstimes
                        ? Number(resaForm.participantsEstimes)
                        : null,
                      equipementsRequis: resaForm.equipementsRequis.map((e) => ({
                        idArticle: e.idArticle,
                        quantite: Number(e.quantite),
                      })),
                    });
                    setResaForm((f) => ({
                      ...f,
                      titre: '',
                      dateDebut: '',
                      dateFin: '',
                      participantsEstimes: '',
                      equipementsRequis: [],
                    }));
                  }, 'Réservation enregistrée')
                }
                className="academic-panel-btn academic-panel-btn--primary"
              >
                Réserver
              </button>
            }
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Salle *">
                <AcademiaSelect
                  fieldSize="sm"
                  value={resaForm.idSalle}
                  onChange={(e) => setResaForm((f) => ({ ...f, idSalle: e.target.value }))}
                >
                  <option value="">Choisir…</option>
                  {salles.map((s) => (
                    <option key={String(s.id)} value={String(s.id)}>
                      {String(s.libelle)} ({String(s.capacite)} pl.)
                    </option>
                  ))}
                </AcademiaSelect>
              </FormField>
              <FormField label="Titre / objet *">
                <input
                  value={resaForm.titre}
                  onChange={(e) => setResaForm((f) => ({ ...f, titre: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Date et heure de début *">
                <input
                  type="datetime-local"
                  value={resaForm.dateDebut}
                  onChange={(e) => setResaForm((f) => ({ ...f, dateDebut: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Date et heure de fin *">
                <input
                  type="datetime-local"
                  value={resaForm.dateFin}
                  onChange={(e) => setResaForm((f) => ({ ...f, dateFin: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
              <FormField label="Participants estimés">
                <input
                  type="number"
                  min={0}
                  value={resaForm.participantsEstimes}
                  onChange={(e) => setResaForm((f) => ({ ...f, participantsEstimes: e.target.value }))}
                  className={fieldInputClass}
                />
              </FormField>
            </div>
            {salleSelectionnee?.equipements && salleSelectionnee.equipements.length > 0 ? (
              <div className="mt-3 rounded border border-gray-200 bg-white p-3">
                <p className="mb-2 text-xs font-bold uppercase text-gray-600">Équipements requis</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {salleSelectionnee.equipements.map((eq) => {
                    const articleId = String(eq.article?.id ?? '');
                    if (!articleId) return null;
                    const current = resaForm.equipementsRequis.find((r) => r.idArticle === articleId);
                    return (
                      <div key={articleId} className="flex items-end justify-between gap-2">
                        <span className="pb-2 text-sm">
                          {String(eq.article?.libelle ?? 'Équipement')} (dispo {Number(eq.quantite ?? 0)})
                        </span>
                        <FormField label="Quantité requise" className="w-28 shrink-0">
                        <input
                          type="number"
                          min={0}
                          className={fieldInputClass}
                          value={current?.quantite ?? '0'}
                          onChange={(e) => {
                            const q = e.target.value;
                            setResaForm((f) => {
                              const next = f.equipementsRequis.filter((r) => r.idArticle !== articleId);
                              if (Number(q) > 0) next.push({ idArticle: articleId, quantite: q });
                              return { ...f, equipementsRequis: next };
                            });
                          }}
                        />
                        </FormField>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="calendar_month" tone="inherit" size={22} />}
            title="Réservations"
            subtitle={`${reservations.length} enregistrement(s)`}
            flush={reservations.length > 0}
          >
          <ul className="academic-panel-list p-4">
            {reservationsPagination.paginatedItems.map((r) => {
              const salle = r.salle as { libelle?: string; capacite?: number };
              return (
                <AcademicPanelListItem key={String(r.id)}>
                  <span className="min-w-0 flex-1 text-sm">
                    <strong>{String(r.titre)}</strong> — {salle?.libelle}
                    <br />
                    <span className="text-on-surface-variant">
                      {formatDateTime(String(r.dateDebut))} → {formatDateTime(String(r.dateFin))} ({String(r.statut)})
                    </span>
                    {(Array.isArray(r.equipementsRequis) ? r.equipementsRequis : []).length > 0 ? (
                      <span className="ml-1 text-xs text-on-surface-variant">
                        · {String((r.equipementsRequis as unknown[]).length)} équipement(s)
                      </span>
                    ) : null}
                  </span>
                  {r.statut === 'PLANIFIEE' ? (
                    <button
                      type="button"
                      className="academic-panel-btn academic-panel-btn--primary"
                      onClick={() =>
                        void runAction(
                          () => patchReservationSalle(String(r.id), { statut: 'CONFIRMEE' }),
                          'Réservation confirmée',
                        )
                      }
                    >
                      Confirmer
                    </button>
                  ) : null}
                </AcademicPanelListItem>
              );
            })}
          </ul>
          <TablePaginationBar
            itemCount={reservations.length}
            currentPage={reservationsPagination.currentPage}
            totalPages={reservationsPagination.totalPages}
            onPageChange={reservationsPagination.onPageChange}
            summary={reservationsPagination.summary}
          />
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'historique' ? (
        <AcademicPanelCard
          icon={<LucideSymbolIcon name="history" tone="inherit" size={22} />}
          title="Historique logistique"
          subtitle={`${historique.length} événement(s)`}
          flush={historique.length > 0}
        >
        <div className="logistique-timeline p-4">
          {historiquePagination.paginatedItems.map((h) => (
            <article key={`${String(h.type)}-${String(h.id)}`} className="logistique-timeline-item">
              <span className="logistique-timeline-item__type">{String(h.type)}</span>
              <p className="logistique-timeline-item__title">{String(h.libelle)}</p>
              <p className="logistique-timeline-item__detail">{String(h.detail)}</p>
              <p className="logistique-timeline-item__date">{formatDateTime(String(h.date))}</p>
            </article>
          ))}
        </div>
        <TablePaginationBar
          itemCount={historique.length}
          currentPage={historiquePagination.currentPage}
          totalPages={historiquePagination.totalPages}
          onPageChange={historiquePagination.onPageChange}
          summary={historiquePagination.summary}
        />
        </AcademicPanelCard>
      ) : null}

      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
    </AdminPageShell>
  );
}
